import type { ReactNode } from "react";

import { Checkbox } from "./checkbox";
import { cn } from "./cn";
import { Icon } from "./icon";
import { Input, Textarea } from "./input";
import { Label } from "./label";
import { Select } from "./select";

// Form controls. Every control has a visible label tied to it with `htmlFor`. A hint and an error are tied with `aria-describedby`; an
// invalid control carries `aria-invalid`. A required control carries `required` (the form has `noValidate`, so the browser shows no popup;
// the page shows its own message).

type FieldOption = Readonly<{ value: string; label: string }>;

type FieldBase = Readonly<{
  id: string;
  label: string;
  hint?: ReactNode;
  error?: string | undefined;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
}>;

function describedBy(id: string, hint: ReactNode, error: string | undefined): string | undefined {
  const parts = [hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : undefined;
}

const hintClasses = "m-0 text-[13px] leading-[18px] text-muted-foreground";

function FieldError({ id, error }: Readonly<{ id: string; error: string }>) {
  return (
    <p id={`${id}-error`} className="m-0 text-sm leading-5 font-semibold text-destructive">
      <span aria-hidden="true">⚠ </span>
      {error}
    </p>
  );
}

function FieldFrame({
  id,
  label,
  hint,
  error,
  required,
  children,
}: FieldBase & Readonly<{ children: ReactNode }>) {
  const hintLine = hint ? (
    <p id={`${id}-hint`} className={hintClasses}>
      {hint}
    </p>
  ) : null;
  return (
    <div className={cn("grid min-w-0 content-start gap-1.5")}>
      <Label htmlFor={id} required={required}>
        {label}
      </Label>
      {children}
      {hintLine}
      {error ? <FieldError id={id} error={error} /> : null}
    </div>
  );
}

type TextFieldProps = FieldBase &
  Readonly<{
    value: string;
    onChange: (value: string) => void;
    type?: "text" | "number" | "date" | "time" | "datetime-local" | "url" | "search";
    inputMode?: "text" | "numeric" | "decimal" | "search";
    maxLength?: number;
    placeholder?: string;
    autoComplete?: string;
    list?: string;
    /** For `number`, `date` and `time`: the smallest and the largest value the control takes. */
    min?: number | string;
    max?: number | string;
  }>;

export function TextField(props: TextFieldProps) {
  const { id, value, onChange, type = "text", error, hint, required, disabled, readOnly } = props;
  const input = (
    <Input
      id={id}
      name={id}
      type={type}
      value={value}
      onChange={(event) => onChange(event.currentTarget.value)}
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy(id, hint, error)}
      required={required}
      disabled={disabled}
      readOnly={readOnly}
      inputMode={props.inputMode}
      maxLength={props.maxLength}
      placeholder={props.placeholder}
      autoComplete={props.autoComplete ?? "off"}
      list={props.list}
      min={props.min}
      max={props.max}
      className={type === "search" ? "pl-10" : undefined}
    />
  );
  return (
    <FieldFrame {...props}>
      {type === "search" ? (
        <span className="relative block">
          <Icon
            name="search"
            size="md"
            className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted-foreground"
          />
          {input}
        </span>
      ) : (
        input
      )}
    </FieldFrame>
  );
}

type FileFieldProps = FieldBase &
  Readonly<{
    /** What the file picker offers, for example `image/png,application/pdf`. The server checks the bytes again. */
    accept: string;
    onChange: (file: File | null) => void;
  }>;

/** A file picker with the same label, hint and error as the other controls. It keeps no file itself: the page holds the `File`. */
export function FileField(props: FileFieldProps) {
  const { id, error, hint, required, disabled, accept, onChange } = props;
  return (
    <FieldFrame {...props}>
      <Input
        id={id}
        name={id}
        type="file"
        accept={accept}
        onChange={(event) => onChange(event.currentTarget.files?.[0] ?? null)}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, hint, error)}
        required={required}
        disabled={disabled}
        className="h-auto py-2.5 compact:h-auto compact:py-1"
      />
    </FieldFrame>
  );
}

type TextAreaFieldProps = FieldBase &
  Readonly<{
    value: string;
    onChange: (value: string) => void;
    rows?: number;
    maxLength?: number;
  }>;

export function TextAreaField(props: TextAreaFieldProps) {
  const { id, value, onChange, error, hint, required, disabled, readOnly, rows = 4 } = props;
  return (
    <FieldFrame {...props}>
      <Textarea
        id={id}
        name={id}
        value={value}
        rows={rows}
        onChange={(event) => onChange(event.currentTarget.value)}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, hint, error)}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        maxLength={props.maxLength}
      />
    </FieldFrame>
  );
}

type SelectFieldProps = FieldBase &
  Readonly<{
    value: string;
    onChange: (value: string) => void;
    options: readonly FieldOption[];
    /** A first option with the empty value, for example "Any" or "Choose". */
    placeholder?: string;
  }>;

export function SelectField(props: SelectFieldProps) {
  const { id, value, onChange, options, placeholder, error, hint, required, disabled } = props;
  return (
    <FieldFrame {...props}>
      <Select
        id={id}
        name={id}
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, hint, error)}
        required={required}
        disabled={disabled}
      >
        {placeholder === undefined ? null : <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    </FieldFrame>
  );
}

type CheckboxFieldProps = Readonly<{
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hint?: ReactNode;
  disabled?: boolean;
}>;

export function CheckboxField({
  id,
  label,
  checked,
  onChange,
  hint,
  disabled,
}: CheckboxFieldProps) {
  return (
    <div className="grid min-h-11 grid-cols-[1.25rem_minmax(0,1fr)] items-center gap-x-3 compact:min-h-8">
      <Checkbox
        id={id}
        name={id}
        checked={checked}
        disabled={disabled}
        aria-describedby={hint ? `${id}-hint` : undefined}
        onChange={(event) => onChange(event.currentTarget.checked)}
      />
      <Label htmlFor={id} className="flex min-h-11 items-center font-normal compact:min-h-8">
        {label}
      </Label>
      {hint ? (
        <p id={`${id}-hint`} className={cn(hintClasses, "col-start-2")}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/** A group of related controls under one legend (radio buttons, a set of checkboxes). */
export function FieldGroup({
  legend,
  hint,
  children,
  className,
  bare = false,
}: Readonly<{
  legend: string;
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
  /** No frame: the legend is a plain label above the controls. */
  bare?: boolean;
}>) {
  return (
    <fieldset
      className={cn(
        "m-0 grid min-w-0 gap-2",
        bare ? "border-0 p-0" : "rounded-md border border-border p-4",
        className,
      )}
    >
      <legend className={cn("text-sm font-semibold", bare ? "p-0" : "px-2")}>{legend}</legend>
      {hint ? <p className={hintClasses}>{hint}</p> : null}
      {children}
    </fieldset>
  );
}

/** Moves focus to the first invalid control inside `container`. Returns whether one was found. */
export function focusFirstInvalid(container: HTMLElement | null): boolean {
  const target = container?.querySelector<HTMLElement>('[aria-invalid="true"]');
  if (target === null || target === undefined) return false;
  target.focus();
  return true;
}
